import { AfterViewInit, OnInit, Component, EventEmitter, Inject, Input, Output, ViewChild } from '@angular/core';

import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';

import { Observable, Subject, combineLatest } from 'rxjs';

import { AccesAction, AccesEtat, applicationConstants } from '../../../../app.constants';

import { JSR310ConversionService } from '../../../../shared/jsr310-localdates/services/jsr310-conversion.service';
import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';

import { EventBusService, Events } from '../../../../technical-modules/events/services/event-bus.service';

import { AccesSearchResultsWithCriteria } from '../entities/acces-search-results-with-criteria';
import { AccesSearchResult } from '../entities/acces-search-result';
import { AccesActionRequest } from '../entities/acces-action-request';

import { AccesService } from '../services/acces.service';
import { AccesTLR } from '../entities/acces-tlr.entity';

import { UserTLR } from '../../../humain/user/entities/user-tlr.entity';

import { AccesPilotageEcoOrder } from '../entities/acces-pilotage-eco-order';
import { DemandeFermetureValidationOrder } from '../entities/demande-fermeture-validation-order';

type KeyValue = {
	[key: string]: string;
}

const defaultColumnsNames: string[] = ['numero', 'parcCode', 'ouvrageNoms', 'ouvrageConcernes', 'etat', 'cac', 'entrepriseNoms', 'chargeAcces', 'dateDebut', 'operationIds', 'actions'];

@Component({ template: ''})
export abstract class AccesListeGenericComponentBase<T> implements OnInit, AfterViewInit {
	// Elements de fonctionnement du tableau
	readonly applicationConstants = applicationConstants;
	
	displayedColumns: string[] = [];
	coactivitePilotageEcoSelected: boolean = false;
	resultats: AccesSearchResult[] = [];
	dataSource: MatTableDataSource<AccesSearchResult> = new MatTableDataSource(this.resultats);
	accesAction: typeof AccesAction = AccesAction;
	
	controleSecurite: KeyValue = {
		'HABILITATION_KO': 'Habilitations KO',
		'PDP_KO': 'Doc. de sécurité KO',
		'COACTIVITE_KO': 'Coactivité KO',
		'PLANNING_KO': 'Planning KO',
		'METEO_KO': 'Météo KO',
		'INTERVENANT_INCONNU': 'Intervenant inconnu',
		'VALIDATION_AUTO_ICE': 'Validation automatique'
	};
	
	controleSecuriteLabels: KeyValue = {
		'HABILITATION_KO': 'Habilitations KO, Accord exploitation',
		'PDP_KO': 'Doc. de sécurité KO, Accord exploitation',
		'COACTIVITE_KO': 'Coactivité KO, Accord exploitation',
		'PLANNING_KO': 'Planning KO, Accord exploitation',
		'METEO_KO': 'Météo KO, Accord exploitation',
		'INTERVENANT_INCONNU': 'Intervenant inconnu',
		'VALIDATION_AUTO_ICE': 'Validation automatique, accès ICE'
	};
	
	exportRequest$ = new Subject<void>();
	selectedAcces!: AccesSearchResult;
	showControleSecurite = false;
	
	constructor(
		@Inject(JSR310ConversionService) private jsr310Service: JSR310ConversionService,
		@Inject(EventBusService) private eventBusService: EventBusService,
		@Inject(AccesService) protected accesService: AccesService,
	) {

		// La liste des colonnes affichees dependent de l'utilisateur et de l'etat d'acces pris en charge par la liste
		combineLatest([this.user$, this.etat$]).subscribe(
			([user, etat]) => this.displayedColumns = this.evaluateDisplayedColumns(user, etat)
		)
		
		// Lors d'une demande de rafraichissement de l'affichage, realiser la mise a jour du tableau
		combineLatest([this.refreshCommandsSubject, this.etat$]).subscribe(
			([accesSearchResultsWithCriteria, etat]: [AccesSearchResultsWithCriteria, AccesEtat]) => {
				// introduire une variable qui indiquera si le flag coactivitePilotageEco est allumé
				// si etat = en cours et si le flag coactivitePilotageEco est allumé 
				// 		alors on interroge le serveur pour recupérer les acces coactifs avec les acces en pilotage eco (TN et ICE) 
				//		appeler le service getAccesCoactifsWithPilotageEco en lui fournissant la liste accesList filtée par pilotage eco (TN et ICE)
				//		quand on reçoit les acces coactifs
				//		- on highlight en violet les acces TN et ICE 
				// 		- on ne garde que les acces TN, ICE et coactifs dans la liste
				
				let accesList = accesSearchResultsWithCriteria.searchResults;

				const criteria = accesSearchResultsWithCriteria.criteria;
				
				this.coactivitePilotageEcoSelected = criteria.coactivitePilotageEco;

				// Masquer les accès pilotage eco
				if (criteria.masquerPilotageEco) {
					accesList = accesList.filter(
						(acces: AccesSearchResult) => !(acces.jobExecutionId || acces.ice)
					);
				}

				// Coactivité pilotage économique
				if (etat === AccesEtat.EN_COURS && criteria.coactivitePilotageEco) {

					const pilotageEcoAcces = accesList.filter(
						(acces: AccesSearchResult) => acces.jobExecutionId || acces.ice
					);

					if (pilotageEcoAcces.length === 0) {
						this.displayData([]);
					} else {
						const order = new AccesPilotageEcoOrder();
						order.accesOuvrageIds = pilotageEcoAcces.map( $ => $.ouvrageIds );

						this.accesService.getAccesCoactifsWithPilotageEco(order).subscribe(
							(coactifIds: number[]) => {

								accesList = accesList.filter(
									(acces: AccesSearchResult) =>
										acces.jobExecutionId
										|| acces.ice
										|| coactifIds.includes(acces.id)
								);
								// tri par nom d'ouvrage pour faciliter la lecture du tableau
								accesList = accesList.sort((a, b) => a.ouvrageNoms.localeCompare(b.ouvrageNoms));
								this.displayData(accesList);
							}
						);
					}
				} else {					
					this.displayData(accesList);
				}
			}
		);
		
		// Lors d'une demande d'export
		combineLatest([this.exportRequest$, this.etat$]).subscribe(
			([_request, etat]) => {
				let accesActionRequest: AccesActionRequest<T> = new AccesActionRequest<T>();
				accesActionRequest.request = AccesAction.EXPORTER;
				accesActionRequest.etat = etat;
				this.accesActionRequest.emit(accesActionRequest);
			}
		);	
	}

	@ViewChild(MatPaginator) paginator!: MatPaginator;
	@ViewChild(MatSort) sort!: MatSort;
	
	// Liaison de l'observable servant a commander le rafraichissement des donnees
	protected refreshCommandsSubject: SubjectRelay<AccesSearchResultsWithCriteria> = new SubjectRelay<AccesSearchResultsWithCriteria>();
	@Input() set refreshCommands(obs: Observable<AccesSearchResultsWithCriteria> | undefined) {
		this.refreshCommandsSubject.registerSource(obs);
	}
	
	@Input() title: string = '';
	
	@Input() foldedPannel: boolean = true;
	@Input() pageSize: number = 20;
	@Input() highlightable: boolean = false;
	
	@Input() restrictActionsToCacCc: boolean = false;
	
	protected etat$ = new Subject<AccesEtat>();
	@Input() set etat(value: AccesEtat) {
		if (value != undefined && value != null) {
			this.etat$.next(value);
		}
	}

	private _user!: UserTLR;
	protected user$ = new Subject<UserTLR>();
	@Input() set user(value: UserTLR) {
		if (value != undefined && value != null) {
			this._user = value;
			this.user$.next(value);
		}
	}
	get user(): UserTLR {
		return this._user;
	}
		
	@Output() accesClicked = new EventEmitter<AccesSearchResult>();
	
	@Output() accesActionRequest = new EventEmitter<AccesActionRequest<T>>();
	
	ngOnInit() { }

	ngAfterViewInit() {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		// Preparation de l'accesseur particulier pour le tri des proprietes LocalDate
		this.dataSource.sortingDataAccessor = (item, property) => {
			switch (property) {
				case 'dateDebut': {
					const value = item.dateDebut;
					const valueAsJSDate = this.jsr310Service.fromLocalDate(value);
					return valueAsJSDate;
				}
				case 'dateAutorisation': {
					const value = item.dateAutorisation;
					const valueAsJSDate = value ? this.jsr310Service.fromLocalDate(value) : null;
					return valueAsJSDate;
				}
				case 'dateFin': {
					const value = item.dateFin;
					const valueAsJSDate = this.jsr310Service.fromLocalDate(value);
					return valueAsJSDate;
				}
				case 'dateErreur': {
					const value = item.dateFin;
					const valueAsJSDate = this.jsr310Service.fromLocalDate(value);
					return valueAsJSDate;
				}
				case 'dateFermeture': {
					const value = item.dateFermeture;
					const valueAsJSDate = value ? this.jsr310Service.fromLocalDate(value) : null;
					return valueAsJSDate;
				}
				case 'dateDemandeFermeture': {
					const value = item.dateDemandeFermeture;
					const valueAsJSDate = value ? this.jsr310Service.fromLocalDate(value) : null;
					return valueAsJSDate;
				}
				default: {
					// NB: etrangement, juste faire "item[property]" cause une erreur de compilation
					const value = (item as { [key: string]: any })[property];
					return value;
				}
			}
		};
	}
	
	onRowClicked(acces: AccesSearchResult) {
		this.accesClicked.emit(acces);
	}
	
	requestAction(request: AccesAction, acces: T, mode?: string) {
		let accesActionRequest: AccesActionRequest<T> = new AccesActionRequest<T>();
		accesActionRequest.acces = acces;
		accesActionRequest.request = request;
		if (mode) {
			accesActionRequest.mode = mode;
		}
		this.accesActionRequest.emit(accesActionRequest);
	}
	
	requestAssociation(acces: AccesSearchResult) {
		this.eventBusService.emit(Events.AssociationOperationsModalRequested, acces.id);
	}
	
	getTitle(): string {
		return this.title + ' (' + this.dataSource.data.length + ')';
	}
	
	autorisationPossible(acces: AccesSearchResult) {
		const dateDebut = this.jsr310Service.fromLocalDateTime(acces.dateDebut);
		if (dateDebut) {
			return dateDebut <= new Date(new Date().getTime() + (15 * 60000));
		}
		return false;
	}
	
	refusPossible(acces: AccesSearchResult) {
		const dateDebut = this.jsr310Service.fromLocalDateTime(acces.dateDebut);
		if (dateDebut) {
			return dateDebut <= new Date(new Date().getTime() + (15 * 60000));
		}
		return false;
	}
	
	canDelete(acces: AccesSearchResult) {
		return (acces.etat === 'ETAT_ENREGISTRE' || acces.etat === 'ETAT_DEMANDE_ENTREE') && (
			(this.user.id === acces.cacId || (this.user.id === acces.ccId && acces.typeAcces === 'A_DISTANCE')) // user est cac ou cc sur acces distant
			|| acces.initiateurId === this.user.id  || this.user.profil === 'ADMIN' || this.user.profil === 'REFERENT'
		);
	}
	
	canUpdate(acces: AccesSearchResult) {
		if (acces.ice) {
			return false;
		}
		if (acces.etat === 'ETAT_ENREGISTRE' || acces.etat === 'ETAT_DEMANDE_ENTREE') {
			if (this.user.role === 'EXP' && this.user.antenneId === acces.antenneId) {
				return true;
			}
			if (acces.initiateurId === this.user.id || (this.user.id === acces.cacId || (this.user.id === acces.ccId && acces.typeAcces === 'A_DISTANCE'))) {
				return true;
			}
		} else if (acces.etat == 'ETAT_EN_COURS') {
			return this.user.id === acces.cacId || (this.user.id === acces.ccId && acces.typeAcces === 'A_DISTANCE');
		} else if (acces.etat == 'ETAT_TERMINE') {
			return (this.user.id === acces.cacId || this.user.id === acces.ccId) && acces.daysSinceClosed != null && acces.daysSinceClosed <= 1;
		}
		return false;
	}
	
	protected evaluateDisplayedColumns(user: UserTLR, etat: AccesEtat): string[] {
		let columnNames: string[] = [...defaultColumnsNames];
		switch(etat) {
			case AccesEtat.EN_COURS:
				columnNames.splice(9,0, 'dateAutorisation', 'controleSecurite');
				break;
			case AccesEtat.FERME:
				columnNames.splice(9,0, 'dateAutorisation', 'dateFin', 'controleSecurite');
				break;
			case AccesEtat.REFUSE:
				columnNames.splice(9,0, 'dateFermeture', 'motifRefus');
				break;				
			case AccesEtat.ERREUR:
				columnNames.splice(9,0, 'dateAutorisation', 'dateErreur');
				break;
		}
		if (user.role === 'CCE') {
			columnNames.splice(1,0, 'antenneNom');
		}
		return columnNames;
	}
	
	isCacOrCc(): boolean {
		return this.resultats.some( $ => (this.user.id === $.cacId || (this.user.id === $.ccId && $.typeAcces === 'A_DISTANCE'))  );
	}
	
	split(textToSplit: string, separator: string) {
	     return textToSplit.split(separator);
	}
	
	private displayData(accesList: AccesSearchResult[]) {

		accesList.forEach(
			(acces: AccesSearchResult) => {

				acces.entrepriseNoms = acces.entrepriseNoms
					? acces.entrepriseNoms.split(',').join(', ')
					: '';

				acces.intervenants = acces.intervenantNoms
					? acces.intervenantNoms.split(', ').join(', ')
					: '';

				switch (acces.parcEnergie) {
					case 'Solaire':
						acces.parcIcon = 'fa-th';
						break;

					case 'Eolienne':
						acces.parcIcon = 'fa-brands fa-yelp';
						break;

					case 'Stockage':
						acces.parcIcon = 'fa-regular fa-floppy-disk';
						break;

					default:
						acces.parcIcon = 'fa-genderless';
						break;
				}

				acces.operations = [];

				if (acces.operationIds) {
					acces.operations = acces.operationIds.split(',');
				}

				acces.workorderNumbers = [];
				acces.notificationNumbers = [];

				acces.listWorkorders =
					acces.workorders
						? acces.workorders.split(',')
						: [];

				acces.listNotifications =
					acces.notifications
						? acces.notifications.split(',')
						: [];

				if (acces.listWorkorders.length > 0) {
					acces.listWorkorders.forEach(
						wo => {
							acces.workorderNumbers.push(
								wo.split(':')[0].trim()
							);
						}
					);
				}

				if (acces.listNotifications.length > 0) {
					acces.listNotifications.forEach(
						notification => {
							acces.notificationNumbers.push(
								notification.split(':')[0].trim()
							);
						}
					);
				}
			}
		);

		this.resultats = accesList;
		this.dataSource.data = accesList;
	}
	
	canAccessActions(acces: AccesSearchResult): boolean {
		if (!this.restrictActionsToCacCc) {
			return true;
		}

		return this.user.id === acces.cacId
			|| this.user.id === acces.ccId;
	}
	
	approveDemandeFermeture(acces: AccesTLR) {
		let validationOrder: DemandeFermetureValidationOrder = new DemandeFermetureValidationOrder(
			acces.id, this.jsr310Service.toLocalDateTime(new Date())
		);
		this.accesService.approveDemandeFermeture(validationOrder).subscribe(
			() => {
				this.eventBusService.emit(Events.AccesListChanged, null);
			},
			(error) => {
				console.error(error);
				alert("Erreur lors de l'autorisation de fermeture");
			}
		);
	}

	denyDemandeFermeture(acces: AccesTLR) {
		let validationOrder: DemandeFermetureValidationOrder = new DemandeFermetureValidationOrder(
			acces.id, this.jsr310Service.toLocalDateTime(new Date())
		);
		this.accesService.denyDemandeFermeture(validationOrder).subscribe(
			() => {
				this.eventBusService.emit(Events.AccesListChanged, null);
			},
			(error) => {
				console.error(error);
				alert("Erreur lors du refus de fermeture");
			}
		);
	}
	
	openControleSecurite(acces: AccesSearchResult) {
		this.selectedAcces = acces;
		this.showControleSecurite = true;
	}
}